import { useEffect } from 'react';

interface FAQItem {
  question: string;
  answer: string;
}

interface BreadcrumbItem {
  name: string;
  path: string;
}

interface SEOHeadProps {
  title: string;
  description: string;
  canonicalPath: string;
  pageType: 'website' | 'article' | 'faq';
  articleData?: {
    publishedDate: string;
    modifiedDate: string;
    authorName: string;
  };
  faqData?: FAQItem[];
  breadcrumbData?: BreadcrumbItem[];
}

export default function SEOHead({
  title,
  description,
  canonicalPath,
  pageType,
  articleData,
  faqData,
  breadcrumbData,
}: SEOHeadProps) {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://litbuyspreadsheets.one';
  const fullUrl = `${siteUrl}${canonicalPath}`;
  const ogType = pageType === 'article' || pageType === 'faq' ? 'article' : 'website';

  useEffect(() => {
    document.title = title;

    // --- Meta description ---
    const existingMetaDesc = document.querySelector('meta[name="description"]');
    if (existingMetaDesc) {
      existingMetaDesc.setAttribute('content', description);
    }

    // --- Canonical ---
    const existingCanonical = document.querySelector('link[rel="canonical"]');
    if (existingCanonical) {
      existingCanonical.setAttribute('href', fullUrl);
    }

    // --- Open Graph ---
    const setOrCreateMeta = (selector: string, attr: string, value: string) => {
      const el = document.querySelector(selector);
      if (el) {
        el.setAttribute(attr, value);
      }
    };

    setOrCreateMeta('meta[property="og:title"]', 'content', title);
    setOrCreateMeta('meta[property="og:description"]', 'content', description);
    setOrCreateMeta('meta[property="og:url"]', 'content', fullUrl);
    setOrCreateMeta('meta[property="og:type"]', 'content', ogType);

    // --- Twitter ---
    setOrCreateMeta('meta[name="twitter:title"]', 'content', title);
    setOrCreateMeta('meta[name="twitter:description"]', 'content', description);
    setOrCreateMeta('meta[name="twitter:card"]', 'content', 'summary_large_image');

    // --- Primary JSON-LD (Article / FAQ / WebPage) ---
    const scriptId = 'json-ld-schema';
    let scriptEl = document.getElementById(scriptId) as HTMLScriptElement | null;
    if (!scriptEl) {
      scriptEl = document.createElement('script');
      scriptEl.id = scriptId;
      scriptEl.type = 'application/ld+json';
      document.head.appendChild(scriptEl);
    }

    let jsonLd: Record<string, unknown>;

    if (pageType === 'article' && articleData) {
      jsonLd = {
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: title,
        description: description,
        url: fullUrl,
        datePublished: articleData.publishedDate,
        dateModified: articleData.modifiedDate,
        author: {
          '@type': 'Organization',
          name: articleData.authorName,
        },
        publisher: {
          '@type': 'Organization',
          name: 'LitBuy Spreadsheet Resource Center',
          url: siteUrl,
        },
        mainEntityOfPage: {
          '@type': 'WebPage',
          '@id': fullUrl,
        },
      };
    } else if (pageType === 'faq' && faqData && faqData.length > 0) {
      jsonLd = {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        url: fullUrl,
        mainEntity: faqData.map((item) => ({
          '@type': 'Question',
          name: item.question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: item.answer.replace(/<[^>]*>/g, ''),
          },
        })),
      };
    } else {
      jsonLd = {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: title,
        description: description,
        url: fullUrl,
        publisher: {
          '@type': 'Organization',
          name: 'LitBuy Spreadsheet Resource Center',
          url: siteUrl,
        },
      };
    }

    scriptEl.textContent = JSON.stringify(jsonLd, null, 2);

    // --- BreadcrumbList JSON-LD ---
    const breadcrumbScriptId = 'json-ld-breadcrumb';
    let breadcrumbScriptEl = document.getElementById(breadcrumbScriptId) as HTMLScriptElement | null;

    if (breadcrumbData && breadcrumbData.length > 0) {
      if (!breadcrumbScriptEl) {
        breadcrumbScriptEl = document.createElement('script');
        breadcrumbScriptEl.id = breadcrumbScriptId;
        breadcrumbScriptEl.type = 'application/ld+json';
        document.head.appendChild(breadcrumbScriptEl);
      }

      const breadcrumbJsonLd = {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: breadcrumbData.map((item, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          name: item.name,
          item: `${siteUrl}${item.path}`,
        })),
      };

      breadcrumbScriptEl.textContent = JSON.stringify(breadcrumbJsonLd, null, 2);
    } else if (breadcrumbScriptEl) {
      breadcrumbScriptEl.remove();
    }
  }, [title, description, fullUrl, canonicalPath, pageType, articleData, faqData, siteUrl, ogType, breadcrumbData]);

  return null;
}